import moment from "moment/moment";
import ApiServices from "../api/services";
import Drawer from "../components/Drawer";
import { baseUrl, mediaUrl } from "../utils/config";
import Heading from "../components/Heading";
import Skeleton from "react-loading-skeleton";
import RemoteIcon from "../components/RemoteIcon";
import { images } from "../assets/styles/Images";
import React, { useEffect, useMemo, useState } from "react";
import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/solid";
import { XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, ResponsiveContainer, Bar, BarChart } from "recharts";
import StatusFilter from "../components/StatusFilter";
import axios from "axios";
import countryList from "react-select-country-list";
import { GlobeAltIcon } from "@heroicons/react/24/outline";

// months array
const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const Analytics = () => {
  // useState
  const todayEndDate = moment.utc().endOf("day");
  const todayStartDate = moment.utc().startOf("day");
  const [allNursries, setAllNursries] = useState([]);
  const [allCampuses, setAllCampuses] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [allSettingsList, setAllSettingsList] = useState([]);
  const [barChartLoading, setBarChartLoading] = useState(false);
  const [appliedFiltersCount, setAppliedFiltersCount] = useState(0);
  const [selectedValue, setSelectedValue] = useState("today");
  const [employeeReasonsData, setEmployeeReasonsData] = useState(null);
  const [selectedCampusOption, setSelectedCampusOption] = useState(null);
  const [employeeAnalyticsData, setEmployeeAnalyticsData] = useState(null);
  const [selectedSettingOption, setSelectedSettingOption] = useState(null);
  const [selectedDepartmentOption, setSelectedDepartmentOption] = useState(null);
  const [selectedOccupationOption, setSelectedOccupationOption] = useState(null);
  const [selectedCountryOption, setSelectedCountryOption] = useState(null);
  const [selectedRegionOption, setSelectedRegionOption] = useState(null);
  const [selectedCityOption, setSelectedCityOption] = useState("");

  const countriesList = useMemo(() => countryList().getData(), []);

  const [openUserDrawerModal, setOpenUserDrawerModal] = useState({ open: false, data: null });
  // const [dateRangeForm, setDateRangeForm] = useState({ startDate: todayStartDate.unix(), endDate: todayEndDate.unix() });
  // const [employeeAnalyticDateRange, setEmployeeAnalyticDateRange] = useState({ startDate: todayStartDate.unix(), endDate: todayEndDate.unix() });
  const [dateError, setDateError] = useState("");

  const [selectedFilter, setSelectedFilter] = useState("today");
  const [selectedCampus, setSelectedCampus] = useState({ label: "All", value: "All" });
  const [selectedSetting, setSelectedSetting] = useState({ label: "All", value: "All" });
  const [selectedDepartment, setSelectedDepartment] = useState({ label: "All", value: "All" });
  const [allOccupations, setAllOccupations] = useState([]);
  const [selectedOccupation, setSelectedOccupation] = useState({ label: "All", value: "All" });
  const [selectedCountry, setSelectedCountry] = useState({ label: "All", value: "All" });
  const [selectedRegion, setSelectedRegion] = useState({ label: "All", value: "All" });
  const [selectedCity, setSelectedCity] = useState("");

  const [allCountrys, setAllCountrys] = useState(countriesList);
  const [allRegions, setAllRegions] = useState([]);

  const [selectedReasonsView, setSelectedReasonsView] = useState({ label: "Chart", value: "chart" });
  const [selectedPositiveReasonsView, setSelectedPositiveReasonsView] = useState({ label: "Chart", value: "chart" });

  const getCurrentDate = () => {
    const todayStart = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate(), 0, 0, 0));
    const todayEnd = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate(), 23, 59, 59));

    return { start: todayStart, end: todayEnd };
  };

  const today = getCurrentDate();

  const [dateRange, setDateRange] = useState({
    startDate: Math.floor(today.start.getTime() / 1000),
    endDate: Math.floor(today.end.getTime() / 1000),
  });

  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const name = payload[0].name;
      const value = payload[0].value;
      // Recharts passes the full slice object as payload[0].payload — read the
      // icon straight from the API-provided icon_url (no hardcoded name lookup).
      const iconUrl = payload[0].payload?.icon_url;

      return (
        <div className="relative rounded-20 bg-white p-4 shadow-card">
          <div className="h-6 w-6 rounded-full ">
            <RemoteIcon src={iconUrl} alt={name} className="h-full w-full rounded-full object-contain" />
          </div>
          <p className="label">{`${name} : ${value}%`}</p>
        </div>
      );
    }
    return null;
  };

  const parsePercentage = (percentStr) => {
    if (percentStr === null || percentStr === undefined) return 0;
    if (typeof percentStr === "number") return percentStr;
    // Remove % sign if it exists and parse as float
    return parseFloat(percentStr.toString().replace("%", "")) || 0;
  };

  // Fallback palette for reasons whose API `color` is null (e.g. positive reasons).
  const FALLBACK_COLORS = [
    "#264254", "#FEDEBC", "#FCEBB0", "#F0D2C9", "#FFBF9D", "#B6E7CF", "#B2C7D9", "#F7E6AD",
    "#F6B6B6", "#D6E8DB", "#FFE0C7", "#F5CEDC", "#C3DDC3", "#E7D1F4", "#c9d6e3", "#e8c7fd",
  ];

  // Build pie-chart data from a dynamic reasons array (negative or positive).
  const buildPieData = (reasonsArr) =>
    (reasonsArr || []).map((reason, index) => ({
      name: reason.name,
      value: parsePercentage(reason.percentage),
      color: reason.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
      icon_url: reason.icon_url,
    }));

  // Dynamic feelings & reasons from the API (ordered by sort_order server-side).
  const feelingsList = employeeReasonsData?.feelings || [];
  const negativeReasons = employeeReasonsData?.negative_reasons || [];
  const positiveReasons = employeeReasonsData?.positive_reasons || [];

  // Negative-reasons pie (Meh / Low / Struggling) and the new positive-reasons pie.
  const data2 = buildPieData(negativeReasons);
  const positivePieData = buildPieData(positiveReasons);

  // Polarity split — prefer the dynamic block, fall back to legacy string fields.
  const healthyWidth =
    employeeReasonsData?.polarity_split?.healthy_percentage != null
      ? `${employeeReasonsData.polarity_split.healthy_percentage}%`
      : employeeReasonsData?.healthy_percentage;
  const unhealthyWidth =
    employeeReasonsData?.polarity_split?.unhealthy_percentage != null
      ? `${employeeReasonsData.polarity_split.unhealthy_percentage}%`
      : employeeReasonsData?.unhealthy_percentage;

  // Resolve a users_response row to its feeling code — match on feeling_id first,
  // fall back to the legacy `response` name string when feeling_id is missing.
  const resolveFeelingCode = (row) => {
    const byId = feelingsList.find((f) => f.id === row?.feeling_id);
    if (byId) return byId.code;
    const byName = feelingsList.find((f) => f.name === row?.response);
    return byName?.code;
  };

  // Feeling cards — built from the API feelings[] (auto-grows to 7).
  const categories = feelingsList.map((feeling) => ({
    id: feeling.code,
    feelingId: feeling.id,
    label: feeling.name,
    color: feeling.color || "#264254",
    icon_url: feeling.icon_url,
    count: feeling.count,
  }));

  const getFormattedDataline = (selectedValue, employeeAnalyticsData, startDate, endDate, activeCategory, feelingCodes, resolveCode) => {
    let dataline = [];
    let xAxisLabels = [];

    const getDateString = (date) => moment(date).format("MM/DD/YYYY");

    // Zero-initialised counts object keyed by each dynamic feeling code.
    const emptyCounts = () => feelingCodes.reduce((acc, code) => ({ ...acc, [code]: 0 }), {});

    // Rows for the selected feeling card (matched by code), or all rows when none is active.
    const rows = activeCategory
      ? (employeeAnalyticsData || []).filter((data) => resolveCode(data) === activeCategory)
      : employeeAnalyticsData || [];

    // Increment the bucket at `index` for the row's resolved feeling code.
    const bump = (index, data) => {
      const code = resolveCode(data);
      if (code != null && dataline[index] && dataline[index][code] !== undefined) {
        dataline[index][code] += 1;
      }
    };

    switch (selectedValue) {
      case "today":
      case "yesterday":
        xAxisLabels = Array.from({ length: 24 }, (_, i) => `${i}:00`);
        dataline = xAxisLabels.map((hour) => ({ name: hour, ...emptyCounts() }));
        rows.forEach((data) => bump(moment.unix(data.created_date_unix).hour(), data));
        break;

      case "lastMonth": {
        const endOfLastMonth = moment().subtract(1, "month").endOf("month");
        xAxisLabels = Array.from({ length: endOfLastMonth.date() }, (_, i) => `Day ${i + 1}`);
        dataline = xAxisLabels.map((day) => ({ name: day, ...emptyCounts() }));
        rows.forEach((data) => bump(moment.unix(data.created_date_unix).date() - 1, data));
        break;
      }

      case "thisYear":
      case "lastYear":
        xAxisLabels = Array.from({ length: 12 }, (_, i) => moment().utc().month(i).format("MMM"));
        dataline = xAxisLabels.map((month) => ({ name: month, ...emptyCounts() }));
        rows.forEach((data) => {
          const date = moment.unix(data.created_date_unix);
          const targetYear = selectedValue === "thisYear" ? moment().utc().year() : moment().utc().subtract(1, "year").year();
          if (date.year() === targetYear) bump(date.month(), data);
        });
        break;

      case "lastSevenDays": {
        const start = moment().utc().subtract(7, "days");
        xAxisLabels = Array.from({ length: 8 }, (_, i) => getDateString(start.clone().add(i, "days")));
        dataline = xAxisLabels.map((day) => ({ name: day, ...emptyCounts() }));
        rows.forEach((data) => {
          const index = xAxisLabels.indexOf(getDateString(moment.unix(data.created_date_unix)));
          if (index !== -1) bump(index, data);
        });
        break;
      }

      case "custom":
        if (startDate && endDate) {
          const startMoment = moment.unix(startDate).utc();
          const endMoment = moment.unix(endDate).utc();

          if (startMoment.isSame(endMoment, "day")) {
            // Render hourly data if the start and end dates are the same day
            xAxisLabels = Array.from({ length: 24 }, (_, i) => `${i}:00`);
            dataline = xAxisLabels.map((hour) => ({ name: hour, ...emptyCounts() }));
            rows.forEach((data) => {
              const date = moment.unix(data.created_date_unix);
              if (date.isSame(startMoment, "day")) bump(date.hour(), data);
            });
          } else {
            const diff = endMoment.diff(startMoment, "days");

            if (diff >= 0) {
              xAxisLabels = Array.from({ length: diff + 1 }, (_, i) => getDateString(startMoment.clone().add(i, "days")));
              dataline = xAxisLabels.map((label) => ({ name: label, ...emptyCounts() }));
              rows.forEach((data) => {
                const index = xAxisLabels.indexOf(getDateString(moment.unix(data.created_date_unix)));
                if (index !== -1) bump(index, data);
              });
            }
          }
        }
        break;

      default:
        console.error("Invalid selectedValue:", selectedValue);
        break;
    }

    return { dataline, xAxisLabels };
  };

  const feelingCodes = feelingsList.map((feeling) => feeling.code);
  const { dataline, xAxisLabels } = getFormattedDataline(
    selectedValue,
    employeeAnalyticsData,
    dateRange?.startDate,
    dateRange?.endDate,
    activeCategory,
    feelingCodes,
    resolveFeelingCode,
  );

  const formatYAxis = (value) => `${value}`;
  const formatTooltip = (value) => {
    return value;
  };

  // handle employee analytical categories
  const handleCategoryClick = (label) => {
    if (activeCategory === label) {
      setActiveCategory("");
    } else {
      setActiveCategory(label);
    }
  };

  // get employee stats by id
  const getEmployeeStatsDashboard = (sDate, eDate, sCampus, sSetting, sDepartment, sOccupation, sCountry, sRegion, sCity) => {
    setBarChartLoading(true);
    let payload = {};

    if (sDate != null) payload.start_date = sDate;
    if (eDate != null) payload.end_date = eDate;
    if (sDepartment?.value !== "All") payload.department_id = sDepartment?.value;
    if (sCampus?.value !== "All") payload.campus_id = sCampus?.value;
    if (sSetting?.value !== "All") payload.nursery_id = sSetting?.value;
    if (sOccupation?.value !== "All") payload.occupation_id = sOccupation?.value; // Add occupation
    if (sCountry?.label !== "All") payload.country = sCountry?.label;
    if (sRegion?.value !== "All") payload.state = sRegion?.value;
    if (sCity) payload.city = sCity;

    ApiServices.analytics
      .getEmployeeDashboardStats(payload)
      .then((res) => {
        const { data } = res;
        if (data.code === 200) {
          setBarChartLoading(false);
          setEmployeeReasonsData(data?.data);
          setEmployeeAnalyticsData(data?.data?.users_response);
        }
        setBarChartLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setBarChartLoading(false);
      });
  };

  const getDataByFilter = async (filters) => {
    const filterCount = Object.values(filters).reduce((count, filter) => {
      if (filter !== undefined) {
        if (typeof filter === "object" && filter !== null) {
          const innerCount = Object.values(filter).reduce((innerCount, innerFilter) => (innerFilter !== undefined ? innerCount + 1 : innerCount), 0);
          return count + (innerCount > 0 ? 1 : 0);
        } else {
          return count + 1;
        }
      }
      return count;
    }, 0);

    setAppliedFiltersCount(filterCount);
  };

  const getSettingsWithoutPagination = (occupationValue, countryValue, regionValue, cityValue) => {
    let payload = {
      occupation_id: occupationValue,
      country: countryValue,
      state: regionValue,
      city: cityValue,
    };
    ApiServices.supportTickets
      .getSettingsWithoutPagination(payload)
      .then((res) => {
        const { data, message } = res;
        if (data.code === 200) {
          let settingsData = data?.data?.map((item) => ({
            image: item?.logo,
            label: item.name,
            value: item.id,
          }));

          settingsData = [{ image: "", label: "All", value: "All" }, ...settingsData];

          setAllNursries(settingsData);
        }
      })
      .catch((err) => {});
  };

  const getCampusList = (nurseryValue) => {
    let payload = {
      mode: 1,
      nursery_id: nurseryValue,
    };
    ApiServices.analytics
      .getCampuses(payload)
      .then((res) => {
        const { data, message } = res;
        if (data.code === 200) {
          let settingsData = data?.data?.map((item) => ({
            label: item.name,
            value: item.id,
          }));

          settingsData = [{ label: "All", value: "All" }, ...settingsData];

          setAllCampuses(settingsData);
        }
      })
      .catch((err) => {});
  };

  const getDepartmentList = (campusValue, nurseryValue) => {
    let payload = {
      mode: 1,
      campus_id: campusValue,
      nursery_id: nurseryValue ? nurseryValue : selectedSettingOption?.value,
    };

    ApiServices.analytics
      .getDepartments(payload)
      .then((res) => {
        const { data, message } = res;
        if (data.code === 200) {
          let settingsData = data?.data?.map((item) => ({
            label: item.name,
            value: item.id,
          }));

          settingsData = [{ label: "All", value: "All" }, ...settingsData];

          setAllDepartments(settingsData);
        }
      })
      .catch((err) => {});
  };

  const getOccupationsList = async () => {
    try {
      const response = await axios.get(`${baseUrl}employee/occupations`);

      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        const occupationsArray = response.data.data;

        if (occupationsArray.length === 0) {
          console.warn("API returned empty occupations list");
        } else {
          let formattedData = occupationsArray.map((occupation) => ({
            value: occupation.id,
            label: occupation.name,
          }));

          formattedData = [{ label: "All", value: "All" }, ...formattedData];
          setAllOccupations(formattedData);
        }
      } else {
        console.error("Unexpected API response structure:", response);
      }
    } catch (error) {
      console.error("Failed to fetch occupations:", error);
    }
  };

  const getRegionsList = async (countryValue) => {
    try {
      const response = await axios.get(`https://secure.geonames.org/countryInfoJSON`, {
        params: {
          country: countryValue,
          username: "azeemse517",
        },
      });

      const responseData = response.data.geonames[0]?.geonameId;
      const responseRegion = await axios.get(`https://secure.geonames.org/childrenJSON`, {
        params: {
          geonameId: responseData,
          username: "azeemse517",
        },
      });

      const RegionsFormattedData = responseRegion?.data?.geonames?.map((item) => ({
        value: item.name,
        label: item.name,
      }));

      setAllRegions(RegionsFormattedData);
      console.log("Regions responseData:", responseRegion);
    } catch (error) {
      console.error("Failed to fetch regions:", error);
    }
  };

  useEffect(() => {
    getOccupationsList();
    getSettingsWithoutPagination();
    getCampusList();
    getDepartmentList();
    // Load stats with the current (Today) date range. Previously this was a
    // bare call with no args, which sent no start_date/end_date and returned
    // all-time data despite the "Today" filter being selected.
    getEmployeeStatsDashboard(
      dateRange.startDate,
      dateRange.endDate,
      selectedCampusOption,
      selectedSettingOption,
      selectedDepartmentOption,
      selectedOccupationOption,
      selectedCountryOption,
      selectedRegionOption,
      selectedCityOption,
    );
  }, []);

  // useEffect(() => {
  //   if (selectedSettingOption?.value && selectedSettingOption?.value !== "All") {
  //     getCampusList();
  //   } else {
  //     setAllCampuses([]);
  //     setSelectedCampusOption(null);
  //   }
  // }, [selectedSettingOption]);

  // useEffect(() => {
  //   if (selectedSettingOption?.value && selectedSettingOption?.value !== "All") {
  //     getDepartmentList();
  //   } else {
  //     setAllDepartments([]);
  //     setSelectedDepartmentOption(null);
  //   }
  // }, [selectedSettingOption]);

  useEffect(() => {
    // Trigger the API call when filters are cleared
    if (
      selectedSettingOption === null &&
      selectedCampusOption === null &&
      selectedDepartmentOption === null &&
      selectedOccupationOption === null &&
      selectedCountryOption === null &&
      selectedRegionOption === null &&
      selectedCityOption === null &&
      appliedFiltersCount === 0
    ) {
      getEmployeeStatsDashboard(
        dateRange.startDate,
        dateRange.endDate,
        selectedCampusOption,
        selectedSettingOption,
        selectedDepartmentOption,
        selectedOccupationOption,
        selectedCountryOption,
        selectedRegionOption,
        selectedCityOption,
      );
    }
  }, [
    selectedSettingOption,
    selectedCampusOption,
    selectedDepartmentOption,
    appliedFiltersCount,
    selectedOccupationOption,
    selectedCountryOption,
    selectedRegionOption,
    selectedCityOption,
  ]);

  const clearFilter = () => {
    getCampusList();
    getDepartmentList();
    setSelectedFilter("today");
    setSelectedSetting({ label: "All", value: "All" });
    setSelectedCampus({ label: "All", value: "All" });
    setSelectedDepartment({ label: "All", value: "All" });
    setSelectedOccupation({ label: "All", value: "All" });
    setSelectedCountry({ label: "All", value: "All" });
    setSelectedRegion({ label: "All", value: "All" });
    setSelectedCountryOption(null);
    setSelectedRegionOption(null);
    setSelectedCity("");
    setSelectedCampusOption(null);
    setSelectedDepartmentOption(null);
    setSelectedOccupationOption(null);
    setSelectedSettingOption(null);
    setAppliedFiltersCount(0);
    setAllDepartments([]);
    setAllCampuses([]);
    const todayStart = Math.floor(today.start.getTime() / 1000);
    const todayEnd = Math.floor(today.end.getTime() / 1000);
    setDateRange({
      startDate: todayStart,
      endDate: todayEnd,
    });
    setSelectedValue("today");
    setDateError("");
    setIsFilterApplied(false);
    // Reload stats explicitly with the reset (Today, no filters) values. State
    // setters above are async, so we pass the computed values directly rather
    // than relying on an effect (which doesn't fire — selectedCityOption stays "").
    getEmployeeStatsDashboard(todayStart, todayEnd, null, null, null, null, null, null, "");
  };

  return (
    <div className="relative grid min-h-[83vh] grid-cols-12 gap-4">
      <div className="card col-span-12 space-y-8 ">
        <div className="mx-6 flex flex-row items-center justify-between border-b-[1px] border-b-grayColor200 pb-5">
          <div className="flex flex-wrap items-center gap-4 self-start pt-1.5">
            <div className="flex items-center gap-x-2 rounded-full bg-secondary/10 px-4 py-1">
              <img src={images.AllSettingIcon} alt="filter icon" className={selectedOccupation ? "h-5 w-5 rounded-full" : ""} />
              <h2 className="text-sm font-medium text-secondary">
                {selectedOccupation ? (selectedOccupation?.label === "All" ? "All Occupations" : selectedOccupation?.label) : "All Occupations"}
              </h2>
            </div>
            <div className="flex items-center gap-x-2 rounded-full bg-secondary/10 px-4 py-1">
              <GlobeAltIcon className="h-5 w-5 text-secondary" />
              <h2 className="text-sm font-medium text-secondary">
                {selectedCountry ? (selectedCountry?.label === "All" ? "All Countries" : selectedCountry?.label) : "All Countries"}
              </h2>
            </div>

            <div className="flex items-center gap-x-2 rounded-full bg-secondary/10 px-4 py-1">
              <GlobeAltIcon className="h-5 w-5 text-secondary" />
              <h2 className="text-sm font-medium text-secondary">
                {selectedRegion ? (selectedRegion?.label === "All" ? "All States" : selectedRegion?.label) : "All States"}
              </h2>
            </div>
            <div className="flex items-center gap-x-2 rounded-full bg-secondary/10 px-4 py-1">
              <GlobeAltIcon className="h-5 w-5 text-secondary" />
              <h2 className="text-sm font-medium text-secondary">
                {selectedCity ? (selectedCity === "" ? "All Cities" : selectedCity) : "All Cities"}
              </h2>
            </div>
            <div className="flex items-center gap-x-2 rounded-full bg-secondary/10 px-4 py-1">
              <img
                src={
                  selectedSettingOption && selectedSettingOption?.label !== "All" ? mediaUrl + selectedSettingOption?.image : images.AllSettingIcon
                }
                alt="filter icon"
                className={selectedSettingOption ? "h-5 w-5 rounded-full" : ""}
              />
              <h2 className="text-sm font-medium text-secondary">
                {selectedSettingOption ? (selectedSettingOption?.label === "All" ? "All Settings" : selectedSettingOption?.label) : "All Settings"}
              </h2>
            </div>
            <div className="flex items-center gap-x-2 rounded-full bg-secondary/10 px-4 py-1">
              <img src={images.AllCampusIcon} alt="filter icon" />
              <h2 className="text-sm font-medium text-secondary">
                {selectedCampusOption ? (selectedCampusOption?.label === "All" ? "All Site" : selectedCampusOption?.label) : "All Site"}
              </h2>
            </div>
            <div className="flex items-center gap-x-2 rounded-full bg-secondary/10 px-4 py-1">
              <img src={images.AllDepartmentIcon} alt="filter icon" />
              <h2 className="text-sm font-medium text-secondary">
                {selectedDepartmentOption
                  ? selectedDepartmentOption?.label === "All"
                    ? "All Departments"
                    : selectedDepartmentOption?.label
                  : "All Departments"}
              </h2>
            </div>
            <div className="flex items-center gap-x-2 rounded-full bg-secondary/10 px-4 py-1">
              <img src={images.CalenderIcon} alt="filter icon" />
              <h2 className="text-sm font-medium text-secondary">
                {selectedValue === "today"
                  ? `Today (${moment.unix(dateRange?.startDate).utc().format("MM/DD/YYYY")})`
                  : selectedValue === "yesterday"
                    ? `Yesterday (${moment.unix(dateRange?.startDate).utc().format("MM/DD/YYYY")})`
                    : selectedValue === "lastSevenDays"
                      ? `Last 7 days (${moment.unix(dateRange?.startDate).utc().format("MM/DD/YYYY")} - ${moment.unix(dateRange?.endDate).utc().format("MM/DD/YYYY")})`
                      : selectedValue === "lastMonth"
                        ? `Last month (${moment.unix(dateRange?.startDate).utc().format("MM/DD/YYYY")})`
                        : selectedValue === "thisYear"
                          ? `This year (${moment.unix(dateRange?.startDate).utc().format("MM/DD/YYYY")})`
                          : selectedValue === "lastYear"
                            ? `Last year (${moment.unix(dateRange?.startDate).utc().format("MM/DD/YYYY")})`
                            : selectedValue === "custom"
                              ? `(${moment.unix(dateRange?.startDate).utc().format("MM/DD/YYYY")} - ${moment.unix(dateRange?.endDate).utc().format("MM/DD/YYYY")})`
                              : `Today (${moment().utc().format("MM/DD/YYYY")})`}
              </h2>
            </div>
          </div>
          <div className="shrink-0 self-start">
            {appliedFiltersCount > 0 ? (
              <button
                type="button"
                className="font-poppins flex h-9 w-56 items-center justify-start
                   gap-x-3 rounded-full border  border-lightGreenColor px-4 text-base  font-medium  focus:outline-none"
              >
                <h2
                  className="shrink-0 text-base font-medium text-lightGreenColor"
                  onClick={() => setOpenUserDrawerModal({ open: true, data: null })}
                >
                  {appliedFiltersCount} active filters
                </h2>
                <div className="h-3/4 border-l-2 border-grayColor200"></div>
                <div className="flex items-center gap-x-1">
                  <img src={images.REDCLOSE} alt="Close" className="h-4 w-4" />
                  <h2 className="text-sm text-red-500" onClick={clearFilter}>
                    Clear
                  </h2>
                </div>
              </button>
            ) : (
              <button
                onClick={() => setOpenUserDrawerModal({ open: true, data: null })}
                type="button"
                className="font-poppins inline-flex h-9 w-40 items-center justify-start
                gap-x-5 rounded-full border border-transparent bg-lightGreenColor px-4 text-base  font-medium text-white hover:bg-lightGreenColor/75 focus:outline-none"
              >
                <AdjustmentsHorizontalIcon className="mr-2 h-5 w-5 text-white" /> Filter
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between px-6">
          <div className="w-full space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-secondary-color">
                {healthyWidth} <span className="font-medium">Settled</span>{" "}
              </h2>
              <h2 className="text-xl font-semibold text-secondary-color">
                {unhealthyWidth} <span className="font-medium">Unsettled</span>{" "}
              </h2>
            </div>
            <div className="w-full">
              <div className="relative mb-4 flex h-10 overflow-hidden rounded-full bg-[#F5F5F5] text-xs">
                <div
                  className={`${healthyWidth == "100%" ? "" : "clip-healthy"} `}
                  style={{ width: healthyWidth, backgroundColor: "#8EE1C8" }}
                ></div>
                <div
                  className={` ${unhealthyWidth == "100%" ? "" : "clip-unhealthy"} `}
                  style={{ width: unhealthyWidth, backgroundColor: "#FF968D" }}
                ></div>
              </div>
            </div>
          </div>

          <div className="flex gap-x-3"></div>
        </div>
        <div className="space-y-8 px-6">
          <div className="space-y-8">
            <div className="flex items-center gap-x-4">
              <div className="relative ">
                <Heading heading="Employee Wellness Analytics" />
              </div>
            </div>
            <div className="space-y-8">
              {barChartLoading ? (
                <div className="grid grid-cols-5 gap-x-4">
                  <Skeleton count={1} height={150} borderRadius={16} />
                  <Skeleton count={1} height={150} borderRadius={16} />
                  <Skeleton count={1} height={150} borderRadius={16} />
                  <Skeleton count={1} height={150} borderRadius={16} />
                  <Skeleton count={1} height={150} borderRadius={16} />
                </div>
              ) : (
                <>
                  <div
                    className="-ml-8 grid items-center"
                    style={{ gridTemplateColumns: `repeat(${categories.length || 5}, minmax(0, 1fr))` }}
                  >
                    {categories.map((category) => (
                      <div className="relative" key={category.id} onClick={() => handleCategoryClick(category.id)}>
                        <div
                          className={`flex cursor-pointer flex-col items-center gap-y-2 ${activeCategory && activeCategory !== category.id ? "opacity-50" : ""}`}
                        >
                          <RemoteIcon src={category?.icon_url} alt={category?.label} className="h-12 w-12 object-contain" />
                          <h2 className="text-2xl font-medium text-secondary">{category?.count}</h2>
                          <h2 className="text-sm font-medium uppercase 2xl:text-base" style={{ color: category?.color }}>
                            {category?.label}
                          </h2>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {barChartLoading ? (
                <div className="grid grid-cols-1 gap-4">
                  {[...Array(1)].map((_, index) => (
                    <Skeleton key={index} count={1} height={350} borderRadius={16} />
                  ))}
                </div>
              ) : (
                <>
                  <div className="-ml-10">
                    <ResponsiveContainer width="100%" height={420}>
                      <BarChart
                        width={500}
                        height={300}
                        data={dataline}
                        margin={{
                          top: 5,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                        barSize={30}
                        barGap={5}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" tickLine={false} />
                        <YAxis tickLine={false} tickFormatter={formatYAxis} />
                        <Tooltip formatter={formatTooltip} />

                        {feelingsList.map((feeling) => (
                          <Bar
                            key={feeling.id}
                            dataKey={feeling.code}
                            name={feeling.name}
                            fill={feeling.color || "#264254"}
                            radius={[12, 12, 0, 0]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {/* Reasons + Positive Reasons side by side */}
        <div className="grid grid-cols-12 gap-5 px-6 xl:divide-x">
          <div className="col-span-12 xl:col-span-6">
            <div className="relative flex items-center justify-between pl-6 2xl:pl-12">
              <div className="relative">
                <Heading heading="Reasons" subheading="Meh, Low, Struggling" />
              </div>

              <StatusFilter
                drawerFilter
                options={[
                  { label: "Chart", value: "chart" },
                  { label: "List", value: "list" },
                ]}
                selected={selectedReasonsView}
                setSelected={setSelectedReasonsView}
                width="w-40 h-11"
                report
              />
            </div>
            {selectedReasonsView?.value === "chart" ? (
              barChartLoading ? (
                <div className="px-12 pt-4">
                  <Skeleton count={1} height={450} borderRadius={16} />
                </div>
              ) : (
                <div className="flex flex-col items-center gap-y-3 ">
                  <div>
                    <ResponsiveContainer width={500} height={600}>
                      <PieChart
                        margin={{
                          top: 5,
                          right: 5,
                          bottom: 5,
                          left: 0,
                        }}
                      >
                        <Pie
                          margin={{
                            top: 5,
                            right: 5,
                            bottom: 5,
                            left: 0,
                          }}
                          data={data2.some((item) => item?.value > 0) ? data2 : [{ name: "No Data", value: 100, color: "#d1d5db" }]}
                          height={420}
                          width={500}
                          cx="50%"
                          cy="50%"
                          outerRadius={170}
                          fill="#d6d6d6"
                          dataKey="value"
                        >
                          {(data2.some((item) => item?.value > 0) ? data2 : [{ name: "No Data", value: 100, color: "#d1d5db" }]).map(
                            (entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} stroke="none" className="border-none outline-none" />
                            ),
                          )}
                        </Pie>

                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )
            ) : barChartLoading ? (
              <div className="px-12 pt-4">
                <Skeleton count={1} height={250} borderRadius={16} />
              </div>
            ) : (
              <>
                <div className="grid w-full grid-cols-12 gap-x-1 gap-y-6 pl-12 pt-10">
                  {negativeReasons.length === 0 ? (
                    <p className="col-span-12 text-sm font-medium text-grayColor300">No reasons to display.</p>
                  ) : (
                    negativeReasons.map((reason) => (
                      <div className="col-span-6" key={reason.id}>
                        <div className="flex items-center gap-x-1">
                          <RemoteIcon src={reason.icon_url} alt={reason.name} className="h-10 w-10 object-contain" />
                          <div>
                            <h2 className="font-bold text-secondary">{parsePercentage(reason.percentage)}%</h2>
                            <p className="text-sm font-medium text-grayColor300">{reason.name}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
          {/* Positive reasons pie (Okay / Good / Great) */}
          <div className="col-span-12 xl:col-span-6">
            <div className="relative flex items-center justify-between pl-6 2xl:pl-12">
              <div className="relative">
                <Heading heading="Positive Reasons" subheading="Okay, Good, Great" />
              </div>

              <StatusFilter
                drawerFilter
                options={[
                  { label: "Chart", value: "chart" },
                  { label: "List", value: "list" },
                ]}
                selected={selectedPositiveReasonsView}
                setSelected={setSelectedPositiveReasonsView}
                width="w-40 h-11"
                report
              />
            </div>
            {selectedPositiveReasonsView?.value === "chart" ? (
              barChartLoading ? (
                <div className="px-12 pt-4">
                  <Skeleton count={1} height={450} borderRadius={16} />
                </div>
              ) : (
                <div className="flex flex-col items-center gap-y-3">
                  <ResponsiveContainer width={500} height={600}>
                    <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                      <Pie
                        data={
                          positivePieData.some((item) => item?.value > 0)
                            ? positivePieData
                            : [{ name: "No Data", value: 100, color: "#d1d5db" }]
                        }
                        cx="50%"
                        cy="50%"
                        outerRadius={170}
                        fill="#d6d6d6"
                        dataKey="value"
                      >
                        {(positivePieData.some((item) => item?.value > 0)
                          ? positivePieData
                          : [{ name: "No Data", value: 100, color: "#d1d5db" }]
                        ).map((entry, index) => (
                          <Cell key={`pos-cell-${index}`} fill={entry.color} stroke="none" className="border-none outline-none" />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )
            ) : barChartLoading ? (
              <div className="px-12 pt-4">
                <Skeleton count={1} height={250} borderRadius={16} />
              </div>
            ) : (
              <>
                <div className="grid w-full grid-cols-12 gap-x-1 gap-y-6 pl-12 pt-10">
                  {positiveReasons.length === 0 ? (
                    <p className="col-span-12 text-sm font-medium text-grayColor300">No reasons to display.</p>
                  ) : (
                    positiveReasons.map((reason) => (
                      <div className="col-span-6" key={reason.id}>
                        <div className="flex items-center gap-x-1">
                          <RemoteIcon src={reason.icon_url} alt={reason.name} className="h-10 w-10 object-contain" />
                          <div>
                            <h2 className="font-bold text-secondary">{parsePercentage(reason.percentage)}%</h2>
                            <p className="text-sm font-medium text-grayColor300">{reason.name}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {/* Drawer */}
      <Drawer
        isOpen={openUserDrawerModal.open}
        setIsOpen={(open) => setOpenUserDrawerModal((prev) => ({ ...prev, open }))}
        onApplyFilters={getDataByFilter}
        allSettingsList={allNursries}
        setAllSettingList={setAllNursries}
        allCampuses={allCampuses}
        allCountrys={allCountrys}
        allDepartments={allDepartments}
        allOccupations={allOccupations} // Pass occupations list
        selectedOccupation={selectedOccupation} // Pass selected occupation
        setSelectedOccupation={setSelectedOccupation} // Pass setter for occupation
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
        setDateRange={setDateRange}
        dateRange={dateRange}
        setSelectedDepartmentOption={setSelectedDepartmentOption}
        selectedDepartmentOption={selectedDepartmentOption}
        setSelectedSettingOption={setSelectedSettingOption}
        selectedSettingOption={selectedSettingOption}
        setSelectedCampusOption={setSelectedCampusOption}
        selectedCampusOption={selectedCampusOption}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
        api={getEmployeeStatsDashboard}
        clearFilter={clearFilter}
        setIsFilterApplied={setIsFilterApplied}
        setDateError={setDateError}
        dateError={dateError}
        isFilterApplied={isFilterApplied}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        selectedCampus={selectedCampus}
        setSelectedCampus={setSelectedCampus}
        selectedDepartment={selectedDepartment}
        setSelectedDepartment={setSelectedDepartment}
        selectedSetting={selectedSetting}
        setSelectedSetting={setSelectedSetting}
        getDepartmentList={getDepartmentList}
        getCampusList={getCampusList}
        setAllDepartments={setAllDepartments}
        setAllCampuses={setAllCampuses}
        selectedOccupationOption={selectedOccupationOption}
        setSelectedOccupationOption={setSelectedOccupationOption}
        getSettingsWithoutPagination={getSettingsWithoutPagination}
        setSelectedCountryOption={setSelectedCountryOption}
        selectedCountryOption={selectedCountryOption}
        getOccupationsList={getOccupationsList}
        selectedRegion={selectedRegion}
        setSelectedRegion={setSelectedRegion}
        setSelectedRegionOption={setSelectedRegionOption}
        selectedRegionOption={selectedRegionOption}
        selectedCity={selectedCity}
        setSelectedCity={setSelectedCity}
        setSelectedCityOption={setSelectedCityOption}
        selectedCityOption={selectedCityOption}
        allRegions={allRegions}
        getRegionsList={getRegionsList}
      />
    </div>
  );
};

export default Analytics;
